import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_NAME = "Hospůdka pod Rablinů";
const SITE_URL = "https://www.hospudkapodrablinu.cz";
const DEFAULT_IMAGE = `${SITE_URL}/img/exterier/exterier-2.jpg`;

type SeoEntry = {
  title: string;
  description: string;
  image?: string;
  type?: "website" | "article";
  keywords?: string;
  noindex?: boolean;
  breadcrumbName?: string;
};

const SEO_MAP: Record<string, SeoEntry> = {
  "/": {
    title: "Hospůdka pod Rablinů | Restaurace, ubytování, svatby a akce v Kašavě",
    description:
      "Hospůdka pod Rablinů v Kašavě nabízí poctivou domácí kuchyni, denní menu, ubytování, svatby, oslavy, firemní akce i fotogalerii areálu v přírodě Hostýnských vrchů.",
    keywords:
      "Hospůdka pod Rablinů, restaurace Kašava, restaurace Zlín, domácí kuchyně, denní menu, ubytování Kašava, svatby Kašava, firemní akce Hostýnské vrchy",
    breadcrumbName: "Domů",
  },
  "/aktuality": {
    title: "Aktuality | Hospůdka pod Rablinů",
    description:
      "Sledujte aktuality, novinky, gastro akce a provozní informace Hospůdky pod Rablinů.",
    type: "article",
    keywords:
      "aktuality restaurace, akce Hospůdka pod Rablinů, novinky Kašava, gastro akce Hostýnské vrchy",
    breadcrumbName: "Aktuality",
  },
  "/galerie": {
    title: "Galerie | Hospůdka pod Rablinů",
    description:
      "Prohlédněte si galerii Hospůdky pod Rablinů, restaurace, zahrady, pokojů, svateb a dalších akcí.",
    keywords:
      "galerie restaurace, fotogalerie Hospůdka pod Rablinů, svatby Kašava, ubytování galerie",
    breadcrumbName: "Galerie",
  },
  "/kontakt": {
    title: "Kontakt | Hospůdka pod Rablinů",
    description:
      "Kontaktní údaje, adresa, mapa a e-mailové spojení pro rezervace, ubytování a akce v Hospůdce pod Rablinů.",
    keywords:
      "kontakt Hospůdka pod Rablinů, Kašava 302, rezervace restaurace, ubytování kontakt",
    breadcrumbName: "Kontakt",
  },
  "/firmy": {
    title: "Firemní akce | Hospůdka pod Rablinů",
    description:
      "Firemní akce, večírky, oslavy a skupinové posezení v Hospůdce pod Rablinů v Kašavě.",
    keywords:
      "firemní akce Kašava, firemní večírek Zlín, oslavy restaurace, skupinové akce Hostýnské vrchy",
    breadcrumbName: "Firemní akce",
  },
  "/svatby": {
    title: "Svatby | Hospůdka pod Rablinů",
    description:
      "Svatby v přírodě Hostýnských vrchů, svatební hostiny, prostory a zázemí Hospůdky pod Rablinů.",
    keywords:
      "svatba Kašava, svatby Zlín, svatební hostina, svatební prostory Hostýnské vrchy",
    breadcrumbName: "Svatby",
  },
  "/ubytovani": {
    title: "Ubytování | Hospůdka pod Rablinů",
    description:
      "Stylové ubytování v Kašavě v přírodě Hostýnských vrchů. Prohlédněte si pokoje a rezervujte pobyt v Hospůdce pod Rablinů.",
    keywords:
      "ubytování Kašava, penzion Hostýnské vrchy, pokoje Kašava, ubytování Zlínsko",
    breadcrumbName: "Ubytování",
  },
  "/wellness": {
    title: "Wellness s vířivkami a saunou | Hospůdka pod Rablinů",
    description:
      "Wellness v Kašavě: 2 vířivky (venkovní pod hvězdami i krytá až pro 5 osob), finská sauna vytápěná dřevem a prosklená odpočívárna s výhledem do valašských lesů a květinových luk.",
    image: `${SITE_URL}/img/wellness/wellness-virivka-venkovni.jpg`,
    keywords:
      "wellness Kašava, vířivka Zlínsko, privátní wellness Hostýnské vrchy, finská sauna na dřevo, wellness s ubytováním Valašsko, sauna Kašava",
    breadcrumbName: "Wellness",
  },
  "/rezervace": {
    title: "Rezervace | Hospůdka pod Rablinů",
    description:
      "Rezervujte si termín pro ubytování, posezení nebo akci v Hospůdce pod Rablinů.",
    keywords:
      "rezervace restaurace Kašava, rezervace ubytování, rezervace akce Hospůdka pod Rablinů",
    breadcrumbName: "Rezervace",
  },
  "/rezervace-ubytovani": {
    title: "Rezervace ubytování | Hospůdka pod Rablinů",
    description:
      "Online poptávka ubytování v Hospůdce pod Rablinů v Kašavě.",
    breadcrumbName: "Rezervace ubytování",
  },
  "/rezervace-akce": {
    title: "Rezervace akce | Hospůdka pod Rablinů",
    description:
      "Nezávazná poptávka svatby, oslavy nebo firemní akce v Hospůdce pod Rablinů.",
    breadcrumbName: "Rezervace akce",
  },
  "/rezervace-wellness": {
    title: "Rezervace wellness | Hospůdka pod Rablinů",
    description:
      "Nezávazná poptávka wellness – vířivky a finská sauna v Hospůdce pod Rablinů.",
    noindex: true,
    breadcrumbName: "Rezervace wellness",
  },
  "/denni-nabidka": {
    title: "Denní nabídka | Hospůdka pod Rablinů",
    description:
      "Aktuální denní nabídka Hospůdky pod Rablinů přehledně online.",
    keywords:
      "denní nabídka Kašava, polední menu, denní menu Hospůdka pod Rablinů",
    breadcrumbName: "Denní nabídka",
  },
  "/jidelni-listek": {
    title: "Jídelní lístek | Hospůdka pod Rablinů",
    description:
      "Jídelní a nápojový lístek Hospůdky pod Rablinů: polévky, speciality, saláty, dezerty, přílohy a nápoje.",
    keywords:
      "jídelní lístek Kašava, nápojový lístek, restaurace menu, domácí kuchyně Hostýnské vrchy",
    breadcrumbName: "Jídelní lístek",
  },
  "/odpoledni-nabidka": {
    title: "Odpolední nabídka | Hospůdka pod Rablinů",
    description:
      "Odpolední nabídka Hospůdky pod Rablinů pro posezení, dezerty a lehčí jídla.",
    breadcrumbName: "Odpolední nabídka",
  },
  "/napojovy-listek": {
    title: "Nápojový lístek | Hospůdka pod Rablinů",
    description:
      "Nápojový lístek Hospůdky pod Rablinů: pivo, víno, limonády, káva a destiláty.",
    keywords:
      "nápojový lístek Kašava, pivo, víno, limonády, káva, restaurace bar",
    breadcrumbName: "Nápojový lístek",
  },
  "/balicky": {
    title: "Ubytovací balíčky | Hospůdka pod Rablinů",
    description:
      "Víkendové a pobytové balíčky v Hospůdce pod Rablinů v přírodě Hostýnských vrchů.",
    breadcrumbName: "Ubytovací balíčky",
  },
  "/nwt-bistro": {
    title: "Denní nabídka | Hospůdka pod Rablinů",
    description:
      "Aktuální denní nabídka Hospůdky pod Rablinů.",
    breadcrumbName: "Denní nabídka",
  },
  "/svatby-prostory": {
    title: "Svatební prostory | Hospůdka pod Rablinů",
    description:
      "Prostory pro svatby, venkovní i vnitřní zázemí a atmosféra Hospůdky pod Rablinů.",
    breadcrumbName: "Svatební prostory",
  },
  "/svatby-hostina": {
    title: "Svatební hostina | Hospůdka pod Rablinů",
    description:
      "Svatební hostina, menu a servis pro svatby v Hospůdce pod Rablinů.",
    breadcrumbName: "Svatební hostina",
  },
  "/fotogalerie-pokoje": {
    title: "Fotogalerie pokojů | Hospůdka pod Rablinů",
    description:
      "Fotogalerie pokojů a ubytování v Hospůdce pod Rablinů.",
    breadcrumbName: "Fotogalerie pokojů",
  },
  "/fotogalerie-restaurace": {
    title: "Fotogalerie restaurace | Hospůdka pod Rablinů",
    description:
      "Fotogalerie restaurace a interiéru Hospůdky pod Rablinů.",
    breadcrumbName: "Fotogalerie restaurace",
  },
  "/fotogalerie-exterier": {
    title: "Fotogalerie exteriéru | Hospůdka pod Rablinů",
    description:
      "Fotogalerie zahrady, exteriéru a okolí Hospůdky pod Rablinů.",
    breadcrumbName: "Fotogalerie exteriéru",
  },
  "/fotogalerie-wellness": {
    title: "Fotogalerie wellness | Hospůdka pod Rablinů",
    description:
      "Fotogalerie wellness Hospůdky pod Rablinů – vířivky, finská sauna na dřevo a prosklená odpočívárna.",
    breadcrumbName: "Fotogalerie wellness",
  },
  "/fotogalerie-speciality": {
    title: "Fotogalerie specialit | Hospůdka pod Rablinů",
    description:
      "Fotogalerie specialit a poctivé domácí kuchyně Hospůdky pod Rablinů.",
    breadcrumbName: "Fotogalerie specialit",
  },
  "/fotogalerie-sladky-bar": {
    title: "Fotogalerie sladkého baru | Hospůdka pod Rablinů",
    description:
      "Fotogalerie sladkého baru – dorty, zákusky a sladké rauty na svatby i oslavy.",
    breadcrumbName: "Fotogalerie sladkého baru",
  },
  "/fotogalerie-rauty": {
    title: "Fotogalerie rautů | Hospůdka pod Rablinů",
    description:
      "Fotogalerie rautů, oslav a firemních akcí v Hospůdce pod Rablinů.",
    breadcrumbName: "Fotogalerie rautů",
  },
  "/fotogalerie-akce-duo-jamaha": {
    title: "Duo Jamaha | Fotogalerie | Hospůdka pod Rablinů",
    description:
      "Fotogalerie z akcí Duo Jamaha v Hospůdce pod Rablinů.",
    breadcrumbName: "Duo Jamaha",
  },
  "/sladky-bar-fotogalerie": {
    title: "Sladký bar | Fotogalerie | Hospůdka pod Rablinů",
    description:
      "Fotogalerie sladkého baru a cateringového servisu Hospůdky pod Rablinů.",
    breadcrumbName: "Sladký bar",
  },
  "/svatby-fotogalerie": {
    title: "Svatební fotogalerie | Hospůdka pod Rablinů",
    description:
      "Fotogalerie svateb, hostin a svatební atmosféry v Hospůdce pod Rablinů.",
    breadcrumbName: "Svatební fotogalerie",
  },
  "/fotogalerie_svatby": {
    title: "Fotogalerie svateb | Hospůdka pod Rablinů",
    description:
      "Fotogalerie svateb v Hospůdce pod Rablinů.",
    breadcrumbName: "Fotogalerie svateb",
  },
  "/admin": {
    title: "CMS administrace | Hospůdka pod Rablinů",
    description: "Interní administrace obsahu.",
    noindex: true,
    breadcrumbName: "Administrace",
  },
  "/mobile-fotogalerie-ubytovani": {
    title: "Mobilní fotogalerie ubytování | Hospůdka pod Rablinů",
    description: "Duplicitní mobilní galerie ubytování.",
    noindex: true,
  },
  "/mobile-fotogalerie-restaurace": {
    title: "Mobilní fotogalerie restaurace | Hospůdka pod Rablinů",
    description: "Duplicitní mobilní galerie restaurace.",
    noindex: true,
  },
  "/mobile-fotogalerie-duo-jamaha": {
    title: "Mobilní galerie Duo Jamaha | Hospůdka pod Rablinů",
    description: "Duplicitní mobilní galerie.",
    noindex: true,
  },
  "/mobile-fotogalerie-exterier": {
    title: "Mobilní galerie exteriéru | Hospůdka pod Rablinů",
    description: "Duplicitní mobilní galerie exteriéru.",
    noindex: true,
  },
  "/mobile-fotogalerie-nase-speciality": {
    title: "Mobilní galerie specialit | Hospůdka pod Rablinů",
    description: "Duplicitní mobilní galerie specialit.",
    noindex: true,
  },
  "/mobile-fotogalerie-sladky-bar": {
    title: "Mobilní galerie sladkého baru | Hospůdka pod Rablinů",
    description: "Duplicitní mobilní galerie sladkého baru.",
    noindex: true,
  },
  "/mobile-fotogalerie-raut": {
    title: "Mobilní galerie rautů | Hospůdka pod Rablinů",
    description: "Duplicitní mobilní galerie rautů.",
    noindex: true,
  },
  "/mobile-fotogalerie-wellness": {
    title: "Mobilní galerie wellness | Hospůdka pod Rablinů",
    description: "Duplicitní mobilní galerie wellness a sauny.",
    noindex: true,
  },
};

function buildBreadcrumbJsonLd(pathname: string, name: string) {
  const items = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Domů",
      item: SITE_URL,
    },
  ];

  if (pathname !== "/") {
    items.push({
      "@type": "ListItem",
      position: 2,
      name,
      item: `${SITE_URL}${pathname}`,
    });
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

function buildLocalBusinessJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    name: SITE_NAME,
    url: SITE_URL,
    image: [DEFAULT_IMAGE, `${SITE_URL}/img/logo-hpr-main-1.png`],
    telephone: "+420608608101",
    email: "info@hospudkapodrablinu.cz",
    servesCuisine: ["Česká kuchyně", "Regionální speciality", "Domácí kuchyně"],
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Kašava 302",
      postalCode: "763 19",
      addressLocality: "Kašava",
      addressCountry: "CZ",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 49.297828,
      longitude: 17.768988,
    },
    sameAs: [
      "https://www.instagram.com/hospudka_pod_rablinu/",
      "https://www.facebook.com/hospudkapodrablinuofficial/",
      "https://www.google.com/search?q=hospudka+pod+rablinu",
    ],
  };
}

function buildWebsiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    inLanguage: "cs-CZ",
  };
}

/* ============================================================
   Zápis do <head> děláme imperativně.
   Důvod: react-helmet v6 pod React 18 (createRoot + StrictMode)
   své side-efekty neaplikuje, takže title/canonical/OG/JSON-LD
   se do dokumentu vůbec nedostaly.
   Všechny značky spravované tímto souborem nesou data-seo="managed",
   aby se daly při změně routy spolehlivě uklidit.
============================================================ */
const MANAGED = "data-seo";

function setMeta(attr: "name" | "property", key: string, content?: string) {
  const selector = `meta[${attr}="${key}"]`;
  const existing = document.head.querySelector<HTMLMetaElement>(selector);

  if (!content) {
    if (existing?.getAttribute(MANAGED) === "managed") existing.remove();
    return;
  }

  const el = existing ?? document.createElement("meta");
  el.setAttribute(attr, key);
  el.setAttribute("content", content);
  if (!existing) {
    el.setAttribute(MANAGED, "managed");
    document.head.appendChild(el);
  }
}

function setCanonical(href: string) {
  const el =
    document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]') ??
    document.createElement("link");
  el.setAttribute("rel", "canonical");
  el.setAttribute("href", href);
  if (!el.isConnected) {
    el.setAttribute(MANAGED, "managed");
    document.head.appendChild(el);
  }
}

function setJsonLd(blocks: object[], group: string) {
  document.head
    .querySelectorAll(`script[${MANAGED}="${group}"]`)
    .forEach((node) => node.remove());

  blocks.forEach((data) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute(MANAGED, group);
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  });
}

/**
 * Přidá na konkrétní stránce další strukturovaná data (JSON-LD).
 * Použití: useJsonLd([faqSchema, serviceSchema], "wellness");
 */
export function useJsonLd(blocks: object[], group: string) {
  const serialized = JSON.stringify(blocks);

  useEffect(() => {
    setJsonLd(JSON.parse(serialized), `page-${group}`);
    return () => setJsonLd([], `page-${group}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, group]);
}

const SeoManager: React.FC = () => {
  const { pathname } = useLocation();
  const seo = SEO_MAP[pathname] ?? {
    title: `${SITE_NAME} | Restaurace, ubytování a akce`,
    description:
      "Hospůdka pod Rablinů v Kašavě: restaurace, ubytování, svatby, oslavy a denní nabídka.",
    breadcrumbName: "Stránka",
  };

  const canonicalUrl = `${SITE_URL}${pathname === "/" ? "" : pathname}`;
  const robots = seo.noindex ? "noindex, nofollow" : "index, follow";
  const image = seo.image ?? DEFAULT_IMAGE;
  const breadcrumbJsonLd = buildBreadcrumbJsonLd(
    pathname,
    seo.breadcrumbName ?? "Stránka"
  );

  useEffect(() => {
    document.documentElement.setAttribute("lang", "cs");
    document.title = seo.title;

    setMeta("name", "description", seo.description);
    setMeta("name", "keywords", seo.keywords);
    setMeta("name", "author", "GAMPERIOS s.r.o.");
    setMeta("name", "robots", robots);
    setCanonical(canonicalUrl);

    setMeta("property", "og:locale", "cs_CZ");
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:type", seo.type ?? "website");
    setMeta("property", "og:title", seo.title);
    setMeta("property", "og:description", seo.description);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:image", image);

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", seo.title);
    setMeta("name", "twitter:description", seo.description);
    setMeta("name", "twitter:image", image);

    setJsonLd(
      [
        buildWebsiteJsonLd(),
        buildLocalBusinessJsonLd(),
        breadcrumbJsonLd,
      ],
      "global"
    );
  }, [
    seo.title,
    seo.description,
    seo.keywords,
    seo.type,
    robots,
    canonicalUrl,
    image,
    // JSON.stringify kvůli stabilní identitě objektu mezi rendery
    JSON.stringify(breadcrumbJsonLd),
  ]);

  return null;
};

export default SeoManager;
